import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Container } from '../components/layout/Container';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { contactService, ContactFormData } from '../services/contactService';
import toast from 'react-hot-toast';

export const ContactUs: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState<ContactFormData>({
    firstName: '', lastName: '', email: '', phone: '', category: '', title: '', message: '',
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['contact-categories'],
    queryFn: () => contactService.getCategories(),
  });

  const categories = categoriesData?.data?.categories || [];

  const mutation = useMutation({
    mutationFn: (data: ContactFormData) => contactService.submitContactForm(data),
    onSuccess: () => setSubmitted(true),
    onError: (error: any) => {
      const msg = error.response?.data?.message || 'Failed to send message. Please try again.';
      const errors = error.response?.data?.errors;
      if (errors?.length) errors.forEach((e: string) => toast.error(e));
      else toast.error(msg);
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); mutation.mutate(form); };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
        <Container className="py-20">
          <div className="max-w-lg mx-auto text-center">
            <div className="bg-emerald-50 dark:bg-emerald-950 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">Message Sent</h1>
            <p className="text-gray-500 dark:text-gray-400 mb-8">Thanks for reaching out. Our support team will get back to you shortly.</p>
            <Button onClick={() => { setSubmitted(false); setForm({ firstName: '', lastName: '', email: '', phone: '', category: '', title: '', message: '' }); }}>
              Send Another Message
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      {/* Hero */}
      <div className="bg-gradient-to-br from-indigo-600 to-purple-600 text-white py-16">
        <Container className="text-center">
          <h1 className="text-4xl font-bold mb-2">Contact Us</h1>
          <p className="text-indigo-200">Have a question or need help? We're here for you.</p>
        </Container>
      </div>

      <Container className="py-10">
        <div className="max-w-3xl mx-auto">
          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 -mt-10">
            <Card className="flex items-center gap-4">
              <div className="bg-indigo-50 dark:bg-indigo-950 p-3 rounded-xl">
                <Mail className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                <p className="font-medium text-sm text-gray-900 dark:text-gray-100">support@rafflebowl.com</p>
              </div>
            </Card>
            <Card className="flex items-center gap-4">
              <div className="bg-indigo-50 dark:bg-indigo-950 p-3 rounded-xl">
                <Clock className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Response Time</p>
                <p className="font-medium text-sm text-gray-900 dark:text-gray-100">Within 24 hours</p>
              </div>
            </Card>
          </div>

          {/* Form */}
          <Card padding="lg">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-5">Send us a message</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="First Name *" name="firstName" value={form.firstName} onChange={handleChange} required placeholder="John" />
                <Input label="Last Name *" name="lastName" value={form.lastName} onChange={handleChange} required placeholder="Doe" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Email *" name="email" type="email" value={form.email} onChange={handleChange} required placeholder="your@email.com" />
                <Input label="Phone (optional)" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+1234567890" />
              </div>
              <Select
                label="Support Category *"
                name="category"
                value={form.category}
                onChange={handleChange}
                options={[
                  { value: '', label: 'Select a category' },
                  ...categories.map((cat) => ({ value: cat, label: cat })),
                ]}
              />
              <Input label="Message Title *" name="title" value={form.title} onChange={handleChange} required placeholder="Brief summary of your inquiry" />
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Message *</label>
                <textarea id="message" name="message" rows={5} required value={form.message} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent hover:border-gray-300 dark:hover:border-gray-600 transition-colors resize-vertical bg-white dark:bg-gray-900 dark:text-gray-100"
                  placeholder="Tell us how we can help..." />
              </div>
              <Button type="submit" size="lg" isLoading={mutation.isPending} fullWidth>
                <Send className="h-4 w-4 mr-2" /> Send Message
              </Button>
            </form>
          </Card>
        </div>
      </Container>
    </div>
  );
};
